import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { EntityLink } from "scmp-app/components/entity-link";
import { useMyDailyFiveArticle } from "scmp-app/components/my-news/my-daily-five/hooks";
import type { tertiaryArticleContent$key } from "scmp-app/queries/__generated__/tertiaryArticleContent.graphql";

import {
  ActionBar,
  Container,
  ContentWrapper,
  Cover,
  CoverContainer,
  CoverEntityLink,
  Headline,
  StyledNumber,
  Summary,
  TopicLink,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  number: number | string;
  onClick?: (entityId?: string) => void;
  reference: tertiaryArticleContent$key;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  number,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment tertiaryArticleContent on Content {
        ...entityLink
        ...hooksUseMyDailyFiveContent
        ...hooksContentItemProvidersContent
          @arguments(
            withTopicLink: true
            withHeadline: true
            withCoverImage: true
            withCoverImageSize118x118: true
            withActionBar: true
            withSummary: true
          )
        entityId
      }
    `,
    reference,
  );

  const isTrendingVisible = useMyDailyFiveArticle(content);
  const providers = useContentItemProviders(content, { type: "main" });

  return (
    <Container className={className} ref={ref}>
      <StyledNumber number={number} />
      <Wrapper>
        <ContentWrapper>
          <TopicLink>{providers.topicLink?.()}</TopicLink>
          <EntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
            <Headline>{providers.headline({ preferSocialHeadline: true })}</Headline>
          </EntityLink>
        </ContentWrapper>
        <CoverContainer>
          <Summary>{providers.summary()}</Summary>

          <CoverEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
            <Cover>
              {providers.coverImage({
                responsiveVariants: {
                  desktopUp: "size118x118",
                  mobileUp: "size118x118",
                  tabletUp: "size118x118",
                },
              })}
            </Cover>
          </CoverEntityLink>
        </CoverContainer>
        <ActionBar>
          {providers.actionBar({
            isIconVisible: false,
            isTrendingVisible,
          })}
        </ActionBar>
      </Wrapper>
    </Container>
  );
};

Component.displayName = "TertiaryArticle";

export const TertiaryArticle = withHighlightedHeadline(Component);
